import React, { useState } from 'react';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { AGENT } from '@/data/agent';
import { LANDING } from '@/constants/testIds';

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

const ContactForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    'enquiry-type': 'Appraisal request',
    'bot-field': '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in your name, email and phone.');
      return;
    }

    setSubmitting(true);
    try {
      // Netlify Forms accepts standard form-encoded POST to "/"
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'kaid-enquiry', ...form }),
      });
      if (!res.ok) throw new Error(`Form POST failed: ${res.status}`);
      // Full page load (not SPA navigation) so Google Ads / GTM pageview
      // conversion tags fire reliably on the thank-you URL.
      const firstName = form.name.trim().split(' ')[0];
      window.location.assign(`/thank-you?name=${encodeURIComponent(firstName)}`);
      return;
    } catch (err) {
      console.error(err);
      toast.error("Sorry — something went wrong sending your enquiry. Please try again or call Kaid directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid={LANDING.contact}
      className="bg-bayleys-navy text-white relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: contact info */}
        <div className="lg:col-span-5">
          <div className="bayleys-divider-gold mb-6" />
          <p className="text-xs tracking-[0.3em] uppercase text-white/60 font-medium mb-5">
            Get in touch
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight">
            Request a free,<br />no-obligation<br />
            <span className="italic font-medium">appraisal.</span>
          </h2>
          <p className="mt-6 text-lg text-white/75 font-normal leading-relaxed max-w-md">
            Whether you&rsquo;re thinking of selling, buying, or just curious about your home&rsquo;s value &mdash; Kaid is happy to chat.
          </p>

          <div className="mt-12 space-y-6">
            <a
              data-testid={LANDING.contactPhone}
              href={AGENT.phoneHref}
              className="group flex items-start gap-4 hover:text-bayleys-gold transition-colors"
            >
              <span className="flex items-center justify-center w-10 h-10 border border-white/20 group-hover:border-bayleys-gold transition-colors shrink-0">
                <Phone className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs tracking-widest uppercase text-white/50 mb-1">Mobile</p>
                <p className="text-lg font-normal">{AGENT.phone}</p>
              </div>
            </a>

            <a
              data-testid={LANDING.contactEmail}
              href={AGENT.emailHref}
              className="group flex items-start gap-4 hover:text-bayleys-gold transition-colors"
            >
              <span className="flex items-center justify-center w-10 h-10 border border-white/20 group-hover:border-bayleys-gold transition-colors shrink-0">
                <Mail className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs tracking-widest uppercase text-white/50 mb-1">Email</p>
                <p className="text-lg font-normal break-all">{AGENT.email}</p>
              </div>
            </a>

            <a
              data-testid={LANDING.contactAddress}
              href={AGENT.office.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 hover:text-bayleys-gold transition-colors"
            >
              <span className="flex items-center justify-center w-10 h-10 border border-white/20 group-hover:border-bayleys-gold transition-colors shrink-0">
                <MapPin className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs tracking-widest uppercase text-white/50 mb-1">Office — {AGENT.office.name}</p>
                <p className="text-lg font-normal">{AGENT.office.address}</p>
              </div>
            </a>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <div className="bg-white text-bayleys-navy p-8 sm:p-10 lg:p-12">
            {(
              <form
                data-testid={LANDING.form}
                name="kaid-enquiry"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Netlify hidden fields */}
                <input type="hidden" name="form-name" value="kaid-enquiry" />
                <p className="hidden">
                  <label>
                    Don&apos;t fill this out:{' '}
                    <input
                      name="bot-field"
                      value={form['bot-field']}
                      onChange={handleChange}
                    />
                  </label>
                </p>

                <div>
                  <div className="bayleys-divider mb-5" />
                  <h3 className="text-2xl sm:text-3xl font-light tracking-tight">
                    Contact Kaid Maycock
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-bayleys-navy/60 mb-2">
                      Name *
                    </label>
                    <input
                      data-testid={LANDING.formName}
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="bayleys-input w-full bg-transparent border-0 border-b border-bayleys-navy/25 py-2.5 text-base text-bayleys-navy placeholder:text-bayleys-navy/40 focus:outline-none transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-bayleys-navy/60 mb-2">
                      Phone *
                    </label>
                    <input
                      data-testid={LANDING.formPhone}
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      className="bayleys-input w-full bg-transparent border-0 border-b border-bayleys-navy/25 py-2.5 text-base text-bayleys-navy placeholder:text-bayleys-navy/40 focus:outline-none transition-colors"
                      placeholder="021 000 0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-bayleys-navy/60 mb-2">
                    Email *
                  </label>
                  <input
                    data-testid={LANDING.formEmail}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="bayleys-input w-full bg-transparent border-0 border-b border-bayleys-navy/25 py-2.5 text-base text-bayleys-navy placeholder:text-bayleys-navy/40 focus:outline-none transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-bayleys-navy/60 mb-2">
                    I&rsquo;m interested in
                  </label>
                  <select
                    name="enquiry-type"
                    value={form['enquiry-type']}
                    onChange={handleChange}
                    className="bayleys-input w-full bg-transparent border-0 border-b border-bayleys-navy/25 py-2.5 text-base text-bayleys-navy focus:outline-none transition-colors"
                  >
                    <option>Appraisal request</option>
                    <option>Selling my home</option>
                    <option>Buying a home</option>
                    <option>General enquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-bayleys-navy/60 mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid={LANDING.formMessage}
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows="4"
                    className="bayleys-input w-full bg-transparent border-0 border-b border-bayleys-navy/25 py-2.5 text-base text-bayleys-navy placeholder:text-bayleys-navy/40 focus:outline-none transition-colors resize-none"
                    placeholder="Tell Kaid a bit about your property or what you're looking for..."
                  />
                </div>

                <button
                  data-testid={LANDING.formSubmit}
                  type="submit"
                  disabled={submitting}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-bayleys-navy hover:bg-bayleys-navy-hover disabled:opacity-60 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase transition-all duration-200 mt-2"
                >
                  {submitting ? 'Sending…' : 'Send enquiry'}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>

                <p className="text-xs text-bayleys-navy/55 pt-2">
                  By submitting this form you agree to be contacted by Kaid Maycock regarding your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
